/**
 * PostHog feature-flag readiness atom + helpers.
 *
 * Kept out of `posthog-provider.tsx` so that file only exports the provider
 * component (react-refresh/only-export-components ratchet).
 */
import { resetPosthogWorkspaceFlagsSynced } from '@/providers/posthog-workspace-flags';
import { isSaasTelemetryEnabled } from '@/utils/buildProfile';
import { atom, getDefaultStore, useAtomValue } from 'jotai';
import posthog from 'posthog-js';

// TELEMETRY-GATE (design D6): when telemetry is off, PostHog is never
// initialized, so nothing must ever await its readiness — flags are "ready"
// immediately and callers fall back to their code defaults.
export const posthogFlagsReadyAtom = atom(!isSaasTelemetryEnabled);

// Lazy: module-level getDefaultStore() breaks tests that partial-mock jotai
// (same pattern as posthog-workspace-flags). launchModalsConfigAtom imports
// this module, so any suite that loads modalQueueAtoms would otherwise crash.
let jotaiStore: ReturnType<typeof getDefaultStore> | undefined;
const getJotaiStore = () => {
  jotaiStore ??= getDefaultStore();
  return jotaiStore;
};

/**
 * Reset PostHog identity and re-fetch feature flags.
 * Call this during logout instead of calling posthog.reset() directly.
 */
export function invalidatePosthogFlags(): void {
  // TELEMETRY-GATE: nothing to reset or re-fetch when PostHog never initialized.
  if (!isSaasTelemetryEnabled) return;
  posthog.reset();
  getJotaiStore().set(posthogFlagsReadyAtom, false);
  resetPosthogWorkspaceFlagsSynced();
}

/**
 * Returns whether PostHog feature flags have loaded.
 * Use in pages that depend on feature flags for routing or conditional rendering.
 */
export function usePosthogFlagsReady(): boolean {
  return useAtomValue(posthogFlagsReadyAtom);
}
